// @ts-nocheck
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { useAnalytics } from "@store/Framework/Common/Hooks/useAnalytics";
import AdhesiveCaulks from "@store/Library/Components/Icons/AdhesiveCaulks";
import Finishing from "@store/Library/Components/Icons/Finishing";
import JobsiteEssentials from "@store/Library/Components/Icons/JobsiteEssentials";
import OtherFasteners from "@store/Library/Components/Icons/OtherFasteners";
import Screws from "@store/Library/Components/Icons/Screws";
import Tools from "@store/Library/Components/Icons/Tools";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    links = "links",
    iconSize = "iconSize",
}

const enum icons {
    finishing = "Finishing",
    adhesiveCaulks = "AdhesiveCaulks",
    tools = "ScrewDriver",
    screws = "Screw",
    jobsiteEssentials = "JobsiteEssentials",
    otherFasteners = "OtherFasteners",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.iconSize]: number;
        [fields.links]: LinkModel[];
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
    };
}

export interface IconListStyles {
    wrapper?: GridContainerProps;
    iconWrapper?: GridItemProps;
    title?: LinkPresentationProps;
}

export const iconListStyles: IconListStyles = {
    wrapper: {
        gap: 0,
    },
    iconWrapper: {
        width: [6, 4, 4, 2, 2],
        align: "middle",
        css: css`
            border-right: ${pxToRem(1)} solid ${getColor("common.borderDark")};
            padding: ${pxToRem(0)};
            display: flex;
            justify-content: center;
            margin-bottom: ${pxToRem(20)} !important; /* To override gridItem's margin */

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        &:nth-child(even) {
                            border: none;
                        }
                    `,
                    css`
                        &:nth-child(3n + 3) {
                            border: none;
                        }
                    `,
                    css`
                        &:nth-child(3n + 3) {
                            border: none;
                        }
                    `,
                    css`
                        &:last-child {
                            border: none;
                        }
                    `,
                    css`
                        &:last-child {
                            border: none;
                        }
                    `,
                ]);
            }};
        `,
    },
    title: {
        typographyProps: {
            transform: "uppercase",
            css: css`
                ${x =>
                    breakpointValues(
                        [x.theme.design.typography.size.smaller, null, x.theme.design.typography.size.base, null, null],
                        ["font-size"],
                    )}
                text-align: center;
                font-weight: ${x => x.theme.design.typography.weight.bold};
                padding-top: ${pxToRem(10)};
            `,
        },
        css: css`
            ${linkStyles("black", "primary", false, true)}
            ${breakpointValues(pxToRemArray([10, null, 20, null, null]), ["padding"])}
            display: flex;
            flex-direction: column;
            &:hover {
                text-decoration: none;
            }
        `,
    },
};

const styles = iconListStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.finishing:
            return Finishing;
        case icons.adhesiveCaulks:
            return AdhesiveCaulks;
        case icons.tools:
            return Tools;
        case icons.screws:
            return Screws;
        case icons.jobsiteEssentials:
            return JobsiteEssentials;
        case icons.otherFasteners:
            return OtherFasteners;
        default:
            break;
    }

    return "";
};

const IconGrid = ({ fields }: OwnProps) => {
    return (
        <GridContainer {...styles.wrapper}>
            {fields.links.map(link => (
                <GridItem key={newGuid()} {...styles.iconWrapper}>
                    <LinkView link={link} iconSize={fields.iconSize} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

type LinkViewProps = {
    link: LinkModel;
    iconSize: number;
};

const LinkView = ({ link, iconSize }: LinkViewProps) => {
    const iconURL = useGetLink(link.fields.destination).url;
    const { dispatchProductCategoryHomePageEvent } = useAnalytics();

    const iconClickHandler = (url: string | undefined, title: string) => {
        dispatchProductCategoryHomePageEvent(title, url);
    };

    const iconSizeBreakpoints = [`calc(${pxToRem(iconSize)} * .75)`, null, pxToRem(iconSize), null, null];

    return (
        <Link
            onClick={() => iconClickHandler(iconURL, link.fields.title)}
            href={iconURL}
            {...styles.title}
            icon={{
                iconProps: {
                    css: css`
                        ${breakpointValues(iconSizeBreakpoints, ["width", "height"])}

                        svg {
                            ${breakpointValues(iconSizeBreakpoints, ["width", "height"])}
                        }
                    `,
                    src: GetIcon(link.fields.icon),
                },
            }}
        >
            {link.fields.title}
        </Link>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const definition: WidgetDefinition = {
    displayName: "Icon Grid",
    group: "Basic",
    icon: "grip-dots",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Finishing", value: icons.finishing },
                        { displayName: "AdhesiveCaulks", value: icons.adhesiveCaulks },
                        { displayName: "Tools", value: icons.tools },
                        { displayName: "Screws", value: icons.screws },
                        { displayName: "JobsiteEssentials", value: icons.jobsiteEssentials },
                        { displayName: "OtherFasteners", value: icons.otherFasteners },
                    ],
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },

                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },
        {
            name: fields.iconSize,
            displayName: "Icon Size",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            min: 1,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: IconGrid,
    definition,
};

export default widgetModule;
